import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '../Icon';
import { usePrevious } from '@kineticdata/react';
import { toggleFilterField } from '@kineticdata/react/src';

const renderDisplayValue = ({ value, type, options } = {}) => {
  if (type === 'checkbox') {
    return value ? 'Yes' : 'No';
  }

  if (value) {
    if (type === 'select' && Array.isArray(options)) {
      return options.find(o => o.value === value)?.label || value;
    }

    return value;
  }

  return <em className="text-muted">Empty</em>;
};

export const FilterFormLayout = ({
  fields,
  meta,
  fieldSet,
  formKey,
  dirty,
  submit,
  renderers: { filterControl } = {},
}) => {
  const ref = useRef();
  const tableKey = formKey.replace('.filter-form', '');

  // Track filter fields that should be open and editable
  const [openFields, setOpenFields] = useState([]);
  // Handler for toggling a filter field
  const handleOpenField = useCallback(name => {
    setOpenFields(of => (!of.includes(name) ? [...of, name] : of));
    const filters = ref.current;
    // Focus the newly opened field
    window.setTimeout(() => {
      const filterEl = filters?.querySelector(`[name=${name}]`);
      if (filterEl?.focus) {
        filterEl.focus();
      }
    }, 0);
  }, []);

  // Handler for applying the filters
  const applyFilters = useCallback(
    e => {
      // Submit the form which will cause the table to query data using the
      // filter values
      submit(e);
      // Reset all open fields to show the pills instead of fields
      setOpenFields([]);
    },
    [submit],
  );

  // Track the previous filter field set so we can know when it changes
  const previousFieldSet = usePrevious(fieldSet);
  // Check for changes to the filter field set
  useEffect(() => {
    if (
      Array.isArray(fieldSet) &&
      Array.isArray(previousFieldSet) &&
      // If new filter field was added
      fieldSet.some(f => !previousFieldSet.includes(f))
    ) {
      const newField = fieldSet.find(f => !previousFieldSet.includes(f));
      // Toggle the field open so it'll be immediately editable
      if (newField) handleOpenField(newField);
    }
  }, [fieldSet, previousFieldSet, handleOpenField]);

  return (
    fields.size > 0 && (
      <form className="table__filters" ref={ref} onSubmit={applyFilters}>
        {fields
          .map((field, name) =>
            openFields.includes(name) ? (
              // If the field is open, render the field
              field
            ) : (
              // Otherwise render a pill showing the filter value
              <div className="input-group input-group-sm">
                <label className="">{meta.getIn([name, 'label'])}</label>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={() => handleOpenField(name)}
                  title={`Edit ${meta.getIn([name, 'label'])} Filter`}
                  aria-label={`Edit ${meta.getIn([name, 'label'])} Filter`}
                >
                  {renderDisplayValue(meta.getIn([name])?.toJS())}
                </button>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={() => toggleFilterField(tableKey, name)}
                  title={`Remove ${meta.getIn([name, 'label'])} Filter`}
                  aria-label={`Remove ${meta.getIn([name, 'label'])} Filter`}
                >
                  <Icon name="x" md />
                </button>
              </div>
            ),
          )
          .toList()}
        {filterControl?.({ inline: true })}
        <button
          type="submit"
          className="btn btn-primary btn-sm"
          disabled={!dirty}
        >
          Apply
        </button>
        <button
          type="button"
          className="btn btn-link-primary text-dark btn-sm ms-auto"
          onClick={() => toggleFilterField(tableKey, ...fieldSet)}
          title="Remove All Filters"
          aria-label="Remove All Filters"
        >
          <Icon name="x" md />
        </button>
      </form>
    )
  );
};
